import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useLeague } from "../hooks/useLeague";
import { useLeaguePublicContext } from "./LeaguePublicLayout";
import { getGamesUrl } from "../config/api";
import { apiGet } from "../utils/apiClient";
import { Game } from "../types/models";
import { PaginatedResponse } from "../types/api";
import GameListItem from "../Common/GameListItem";

function unwrapPaginated<T>(
  response: PaginatedResponse<T> | T[] | null | undefined,
): T[] {
  if (!response) return [];
  return Array.isArray(response) ? response : response.data;
}

// --- Main component ---------------------------------------------------------

function SchedulePage() {
  const auth = useAuth();
  const { abbr } = useParams<{ abbr: string }>();
  const { currentLeague } = useLeague();
  const { seasonId, gameTypeFilter, dateFilter, teamFilter } =
    useLeaguePublicContext();

  const [allGames, setAllGames] = useState<Game[]>([]);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const limit = 100;

  // Build query params based on filters
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    params.append("skip", skip.toString());

    // Add game type filter if not "all"
    if (gameTypeFilter !== "all") {
      params.append("game_type", gameTypeFilter);
    }

    // Add team filter if selected
    if (teamFilter !== null) {
      params.append("team_id", teamFilter.toString());
    }

    // Add date filter and sort
    const now = new Date().toISOString();
    if (dateFilter === "upcoming") {
      params.append("after", now);
      params.append("sort", "date"); // Ascending: nearest games first
    } else if (dateFilter === "past") {
      params.append("before", now);
      params.append("sort", "-date"); // Descending: most recent first
    } else {
      // "all"
      params.append("sort", "date"); // Ascending: oldest to newest
    }

    return params.toString();
  };

  // Load games for current skip value
  useEffect(() => {
    if (!seasonId) return;

    const loadGames = async () => {
      setLoading(true);
      try {
        const queryParams = buildQueryParams();
        const url = `${getGamesUrl(seasonId)}?${queryParams}`;
        const response = await apiGet<PaginatedResponse<Game>>(url, {
          token: auth.token,
          leagueAbbr: currentLeague?.abbr,
        });

        const newGames = unwrapPaginated(response);

        if (skip === 0) {
          setAllGames(newGames);
        } else {
          setAllGames((prev) => [...prev, ...newGames]);
        }

        if (typeof response === "object" && "total" in response) {
          setTotal(response.total);
        }
      } catch (error) {
        console.error("Failed to load games:", error);
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };

    loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    seasonId,
    skip,
    gameTypeFilter,
    dateFilter,
    teamFilter,
    auth.token,
    currentLeague?.abbr,
  ]);

  // Reset pagination when season or filters change
  useEffect(() => {
    setSkip(0);
    setAllGames([]);
    setTotal(0);
    setInitialLoad(true);
  }, [seasonId, gameTypeFilter, dateFilter, teamFilter]);

  if (!seasonId) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  // Server handles sorting based on dateFilter
  const games = allGames;

  const hasMore = allGames.length < total;

  if (initialLoad) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  return (
    <>
      {games.length === 0 ? (
        <div className="lp-card">
          <div className="lp-card-empty">No games scheduled.</div>
        </div>
      ) : (
        <div className="schedule-grid">
          {games.map((game) => (
            <div key={game.id} className="lp-card">
              <GameListItem game={game} leagueAbbr={abbr} />
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: "16px" }}>
          <button
            onClick={() => setSkip((s) => s + limit)}
            disabled={loading}
            className="lp-load-more-btn"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading...
              </>
            ) : (
              "Load More"
            )}
          </button>
        </div>
      )}
    </>
  );
}

export default SchedulePage;
